import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Eye } from 'lucide-react';
import { ADMIN_BASE } from '../../utils/apiBase';

const API = ADMIN_BASE;

const AdminOrders = () => {
  const { getAuthHeader } = useAdmin();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/orders`, { headers: getAuthHeader() });
      setOrders(res.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, 
        { status },
        { headers: getAuthHeader() }
      );
      toast.success('Status updated!');
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        const res = await axios.get(`${API}/orders/${orderId}`, {
          headers: getAuthHeader()
        });
        setSelectedOrder(res.data);
      }
    } catch (error) {
      toast.error('Update failed');
    }
  };

  const viewOrderDetails = async (orderId) => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}`, {
        headers: getAuthHeader()
      });
      setSelectedOrder(res.data);
    } catch (error) {
      toast.error('Failed to load order details');
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Orders</h1>

      {selectedOrder && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-2xl font-bold">Order #{selectedOrder.id.slice(0, 8)}</h2>
              <p className="text-gray-600">{new Date(selectedOrder.created_at).toLocaleString()}</p>
            </div>
            <button
              onClick={() => setSelectedOrder(null)}
              className="text-gray-600 hover:text-gray-800"
            >
              Close
            </button>
          </div>

          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="font-bold mb-2">Customer Details</h3>
              <p><strong>Name:</strong> {selectedOrder.customer_name}</p>
              <p><strong>Email:</strong> {selectedOrder.customer_email}</p>
              <p><strong>Phone:</strong> {selectedOrder.customer_phone}</p>
              <p><strong>Address:</strong> {selectedOrder.delivery_address}</p>
            </div>
            <div>
              <h3 className="font-bold mb-2">Order Status</h3>
              <select
                value={selectedOrder.order_status || 'pending'}
                onChange={(e) => updateOrderStatus(selectedOrder.id, e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <p className="mt-4"><strong>Payment:</strong> {selectedOrder.payment_status}</p>
              <p><strong>Method:</strong> {selectedOrder.payment_method}</p>
            </div>
          </div>

          <h3 className="font-bold mb-2">Order Items</h3>
          <table className="w-full mb-4">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left">Product</th>
                <th className="px-4 py-2 text-left">Quantity</th>
                <th className="px-4 py-2 text-left">Price</th>
                <th className="px-4 py-2 text-left">Total</th>
              </tr>
            </thead>
            <tbody>
              {selectedOrder.items.map((item, index) => (
                <tr key={index} className="border-t">
                  <td className="px-4 py-2">{item.product_name}</td>
                  <td className="px-4 py-2">{item.quantity}</td>
                  <td className="px-4 py-2">₹{item.price}</td>
                  <td className="px-4 py-2">₹{item.price * item.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="border-t pt-4">
            <div className="flex justify-between mb-2">
              <span>Subtotal:</span>
              <span>₹{selectedOrder.subtotal}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span>Delivery:</span>
              <span>₹{selectedOrder.delivery_charge}</span>
            </div>
            {selectedOrder.discount > 0 && (
              <div className="flex justify-between mb-2 text-green-600">
                <span>Discount:</span>
                <span>- ₹{selectedOrder.discount}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-lg border-t pt-2">
              <span>Total:</span>
              <span>₹{selectedOrder.total}</span>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">Order ID</th>
              <th className="px-4 py-3 text-left">Customer</th>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Total</th>
              <th className="px-4 py-3 text-left">Payment</th>
              <th className="px-4 py-3 text-left">Order Status</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.id} className="border-t">
                <td className="px-4 py-3">{order.id.slice(0, 8)}</td>
                <td className="px-4 py-3">{order.customer_name}</td>
                <td className="px-4 py-3">{new Date(order.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">₹{order.total}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-sm ${
                    order.payment_status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {order.payment_status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                    {order.order_status || 'pending'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => viewOrderDetails(order.id)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Eye size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminOrders;
